import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  TextField,
  Button,
  Link,
  Grid
} from '@mui/material';
import GoogleSignInButton from './GoogleSignInButton';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * SignupPage Component
 * Allows new users to create an account with email/password or Google OAuth
 */
const SignupPage = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form state
  const [signupForm, setSignupForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSignupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!signupForm.firstName.trim()) {
      setError('First name is required');
      return false;
    }

    if (!signupForm.lastName.trim()) {
      setError('Last name is required');
      return false;
    }

    if (!signupForm.companyName.trim()) {
      setError('Company name is required');
      return false;
    }

    if (!signupForm.email.trim()) {
      setError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupForm.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!signupForm.password) {
      setError('Password is required');
      return false;
    }

    if (signupForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (signupForm.password !== signupForm.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  // Handle signup form submission
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      const metadata = {
        full_name: `${signupForm.firstName} ${signupForm.lastName}`.trim(),
        first_name: signupForm.firstName,
        last_name: signupForm.lastName,
        company_name: signupForm.companyName
      };

      const { data, error } = await signUp(
        signupForm.email,
        signupForm.password,
        metadata
      );

      if (error) {
        console.error('Signup error:', error);
        setError(error.message || 'Failed to create account. Please try again.');
        return;
      }

      // Success! Show message and redirect
      setSuccess(true);
      setTimeout(() => {
        navigate('/onboarding');
      }, 1500);
    } catch (error) {
      console.error('Unexpected error during signup:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Handle Google signup success
  const handleGoogleSuccess = () => {
    // Google signup will redirect via OAuth flow
    // Redirect URL is configured in authUtils.js
  };

  const handleGoogleError = (error) => {
    setError(error.message || 'Google sign up failed. Please try again.');
  };

  // Wyspur colors
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#2F6BFD',
    text: '#FFFFFF'
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      bgcolor: wyspurColors.darkNavy,
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Paper elevation={3} sx={{
        bgcolor: wyspurColors.navy,
        borderRadius: 2,
        px: 4,
        py: 4,
        width: '90%',
        maxWidth: '500px',
        backdropFilter: 'blur(10px)'
      }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <img
            src="/logo-full-cropped.png"
            alt="Wyspur AI"
            style={{
              height: '71px',
              width: 'auto',
              objectFit: 'contain',
              maxWidth: '100%',
              margin: '0 auto',
              display: 'block'
            }}
          />
          <Typography variant="h5" sx={{ color: wyspurColors.text, mt: 2, fontWeight: 600 }}>
            Create your account
          </Typography>
          <Typography variant="body2" sx={{ color: wyspurColors.text, opacity: 0.8, mt: 1 }}>
            Start your journey with Wyspur AI
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Account created successfully! Redirecting to onboarding...
          </Alert>
        )}

        {/* Signup Form */}
        <Box component="form" onSubmit={handleSignupSubmit} sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={signupForm.firstName}
                onChange={handleFormChange}
                required
                variant="filled"
                InputLabelProps={{ sx: { color: wyspurColors.text } }}
                InputProps={{ sx: { color: wyspurColors.text } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={signupForm.lastName}
                onChange={handleFormChange}
                required
                variant="filled"
                InputLabelProps={{ sx: { color: wyspurColors.text } }}
                InputProps={{ sx: { color: wyspurColors.text } }}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label="Company Name"
            name="companyName"
            value={signupForm.companyName}
            onChange={handleFormChange}
            required
            margin="normal"
            variant="filled"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{ sx: { color: wyspurColors.text } }}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={signupForm.email}
            onChange={handleFormChange}
            required
            margin="normal"
            variant="filled"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{ sx: { color: wyspurColors.text } }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={signupForm.password}
            onChange={handleFormChange}
            required
            margin="normal"
            variant="filled"
            helperText="At least 6 characters"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{ sx: { color: wyspurColors.text } }}
            FormHelperTextProps={{ sx: { color: 'rgba(255, 255, 255, 0.6)' } }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={signupForm.confirmPassword}
            onChange={handleFormChange}
            required
            margin="normal"
            variant="filled"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{ sx: { color: wyspurColors.text } }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={success}
            sx={{
              mt: 3,
              mb: 2,
              bgcolor: wyspurColors.blue,
              '&:hover': { bgcolor: `${wyspurColors.blue}dd` },
              py: 1.5,
              fontWeight: 600
            }}
          >
            Create Account
          </Button>
        </Box>

        <Typography variant="body2" align="center" sx={{ mb: 2, color: wyspurColors.text }}>
          or
        </Typography>

        <Box sx={{ mb: 3 }}>
          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            buttonText="Continue with Google"
          />
        </Box>

        <Typography variant="body2" sx={{ color: wyspurColors.text, textAlign: 'center' }}>
          Already have an account?{' '}
          <Link href="/login" sx={{ color: wyspurColors.blue, fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default SignupPage;
